import { LanguageService } from '@/core/services';
import { RoleType } from '@/features/administration/user-profile/types/profile';
import { Language } from '@/shared/models/language';
import { PersonalizationService } from '@/shared/services/personalization.service';
import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';

export type GuidelineCategory = 'general' | 'subloan' | 'questionnaire';

@Injectable({
  providedIn: 'root',
})
export class UserGuidelinesFacadeService {
  private readonly languageService = inject(LanguageService);
  private readonly personalizationService = inject(PersonalizationService);

  private readonly guidelinesMap = new Map<GuidelineCategory, any[]>(
    Object.entries({
      general: [
        {
          titleEn: 'How to manage users?',
          titleEs: '',
          titleFr: 'Comment gérer les utilisateurs ?',
          baseFileName: 'User management by Administrator',
          typeId: [RoleType.Fim, RoleType.Pi],
        },
        {
          titleEn: 'How to sync contacts from fimflow to fimpact?',
          titleEs: 'Sincronización de organizaciones y contactos de Fimflow a Fimpact',
          titleFr: '',
          baseFileName: 'Organization and Contacts sync between fimflow and fimpact',
          typeId: [RoleType.Fim],
        },
        {
          titleEn: 'How to log in?',
          titleEs: 'Instructions de connexion',
          titleFr: 'Comment se connecter ?',
          baseFileName: 'Login Instructions',
          typeId: [RoleType.Fim, RoleType.Pi],
        },
        {
          titleEn: 'How new users can register?',
          titleEs: 'Registro de nuevos usuarios',
          titleFr: "Comment les nouveaux utilisateurs peuvent s'inscrire ?",
          baseFileName: 'New User Registration',
          typeId: [RoleType.Fim, RoleType.Pi],
        },
        {
          titleEn: 'How to create new users?',
          titleEs: 'Creación de un nuevo usuario por un usuario IM',
          titleFr: '',
          baseFileName: 'Creating a New User',
          typeId: [RoleType.Fim],
        },
      ],
      subloan: [
        {
          titleEn: 'How to submit data?',
          titleEs: 'Descripción general de la solicitud de datos y envío por mensajería instantánea',
          titleFr: 'Comment soumettre des données ?',
          baseFileName: 'Data Request Overview and Submission',
          typeId: [RoleType.Pi, RoleType.Fim],
        },
        {
          titleEn: 'FAQs- fimpact system',
          titleEs: '',
          titleFr: 'FAQs- fimpact system',
          baseFileName: 'FAQ - fimpact',
          typeId: [RoleType.Fim, RoleType.Pi],
        },
        {
          titleEn: 'EBF LAC- manual of codes',
          titleEs: 'IT Gov 02 -fimpact.ecobusiness Manual',
          titleFr: '',
          baseFileName: 'fimpact.ecobusiness LAC Manual_4',
          typeId: [RoleType.Pi, RoleType.Fim],
        },
        {
          titleEn: 'How to confirm/reject submmited data?',
          titleEs: 'Confirmación o rechazo de la solicitud de datos por parte de IM',
          titleFr: '',
          baseFileName: 'Data Request Confirmation or Rejection',
          typeId: [RoleType.Fim],
        },
        {
          titleEn: 'How to waive or complete a request?',
          titleEs: '',
          titleFr: '',
          baseFileName: 'How to waive or complete a request',
          typeId: [RoleType.Fim],
        },
        {
          titleEn: 'EFSE manual of codes-Aggregate',
          titleEs: '',
          titleFr: '',
          baseFileName: 'fimpact.EFSE Aggregate Manual_4',
          typeId: [RoleType.Pi, RoleType.Fim],
        },
        {
          titleEn: 'EFSE manual of codes-List',
          titleEs: '',
          titleFr: '',
          baseFileName: 'fimpact.EFSE List Manual_4',
          typeId: [RoleType.Pi, RoleType.Fim],
        },
        {
          titleEn: 'EFSE- How to use the categories of use of proceeds?',
          titleEs: '',
          titleFr: '',
          baseFileName: 'Fimpact3.1_EFSE_Instructions_PLIs',
          typeId: [RoleType.Pi, RoleType.Fim],
        },
        {
          titleEn: 'SANAD- manual of codes English',
          titleEs: '',
          titleFr: 'SANAD- Guide des codes',
          baseFileName: 'fimpact.SANAD Manual_4',
          typeId: [RoleType.Pi, RoleType.Fim],
        },
        {
          titleEn: 'SANAD- How to use the categories of use of proceeds?',
          titleEs: '',
          titleFr: "Comment utiliser les catégories d'utilisation des fonds ?",
          baseFileName: 'Fimpact3.1_SANAD_Instructions_PIs',
          typeId: [RoleType.Pi, RoleType.Fim],
        },
        {
          titleEn: 'GGF- manual of codes',
          titleEs: '',
          titleFr: '',
          baseFileName: 'fimpact.GGF Manual_4',
          typeId: [RoleType.Pi, RoleType.Fim],
        },
        {
          titleEn: 'EBF SSA- manual of codes',
          titleEs: '',
          titleFr: '',
          baseFileName: 'fimpact.ecobusiness SSA Manual_4',
          typeId: [RoleType.Pi, RoleType.Fim],
        },
      ],
      questionnaire: [
        {
          titleEn: 'How to fill out the questionnaire',
          titleEs: 'Cómo diligenciar el cuestionario',
          titleFr: 'Comment remplir le questionnaire',
          baseFileName: 'How to fill out',
          typeId: [RoleType.Pi, RoleType.Fim],
        },
        {
          titleEn: 'How to submit or resubmit the questionnaires',
          titleEs: 'Cómo enviar y reenviar los cuestionarios',
          titleFr: 'Comment soumettre ou resoumettre les questionnaires',
          baseFileName: 'How to submit or resubmit',
          typeId: [RoleType.Pi, RoleType.Fim],
        },
        {
          titleEn: 'Where to find the questionnaires',
          titleEs: 'Dónde encontrar los cuestionarios',
          titleFr: 'Où trouver les questionnaires',
          baseFileName: 'Where to find',
          typeId: [RoleType.Pi, RoleType.Fim],
        },
        {
          titleEn: 'Where to track the status of the questionnaires',
          titleEs: 'Dónde ver el estado de los cuestionarios',
          titleFr: 'Où suivre le statut des questionnaires',
          baseFileName: 'Where to track the status',
          typeId: [RoleType.Pi, RoleType.Fim],
        },
        {
          titleEn: 'How to add collaborators',
          titleEs: 'Cómo agregar colaboradores',
          titleFr: 'Comment ajouter des collaborateurs',
          baseFileName: 'How to add collaborators',
          typeId: [RoleType.Pi, RoleType.Fim],
        },
      ],
    }) as [GuidelineCategory, any[]][],
  );

  currentLanguage = this.languageService.currentLanguage;
  role = toSignal(this.personalizationService.profiles$.pipe(map((t) => t?.role?.typeId ?? null)), {
    initialValue: null,
  });

  guidelines = computed(() => {
    const userRole = this.role();
    const currentLang = this.currentLanguage();
    if (userRole === null) {
      return this.guidelinesMap;
    }
    const mapCopy = new Map(this.guidelinesMap);
    mapCopy.forEach((value, key) => {
      if (key === 'questionnaire') {
        mapCopy.set(key, value.map(item => {
          const suffix = currentLang === Language.FR ? 'fr' : currentLang === Language.ES ? 'es' : 'en';
          const title = currentLang === Language.FR ? item.titleFr : currentLang === Language.ES ? item.titleEs : item.titleEn;
          return {
            ...item,
            title,
            pdf: `assets/manuals/questionnaires/${encodeURIComponent(`${item.baseFileName}/${suffix}.pdf`)}`,
          };
        }));
      } else {
        mapCopy.set(
          key,
          value
            .filter((item) => item.typeId.includes(userRole))
            .filter((item) => {
              if (currentLang === Language.FR) {
                return item.titleFr;
              } else if (currentLang === Language.ES) {
                return item.titleEs;
              } else {
                return item.titleEn;
              }
            })
            .map((item) => {
              let fileName = '';
              let title = '';
  
              if (currentLang === Language.FR) {
                fileName = `${item.baseFileName}-FRA.pdf`;
                title = item.titleFr;
              } else if (currentLang === Language.ES) {
                fileName = `${userRole === RoleType.Pi ? `${item.baseFileName}-PI-SPA.pdf` : `${item.baseFileName}-IM-SPA.pdf`}`;
                title = item.titleEs;
              } else {
                fileName = `${item.baseFileName}.pdf`;
                title = item.titleEn;
              }
  
              return {
                title,
                pdf: `assets/manuals/${encodeURIComponent(fileName)}`,
                typeId: item.typeId,
              };
            }),
        );
      }
    });
    return mapCopy;
  });

  guidelineCategories = computed(() => {
    return Array.from(this.guidelines().keys()) ?? [];
  });

  getIconByCategory(category: GuidelineCategory) {
    switch (category) {
      case 'general':
        return 'radixQuestionMarkCircled';
      case 'subloan':
        return 'radixFileText';
      case 'questionnaire':
        return 'radixClipboard';
    }
  }

  getTitleByCategory(category: GuidelineCategory) {
    switch (category) {
      case 'general':
        return 'user_guidelines.general';
      case 'subloan':
        return 'user_guidelines.subloan';
      case 'questionnaire':
        return 'user_guidelines.questionnaires';
    }
  }
}
